import React, { useLayoutEffect, useState, useRef, useEffect } from "react";
import { motion, useMotionValue } from "framer-motion";
import CarouselItem from "./CarouselItem";
import "./template-preview.css";

const DRAG_BUFFER = 0;
const VELOCITY_THRESHOLD = 500;
const GAP = 16;
const SPRING_OPTIONS = { type: "spring", stiffness: 300, damping: 30 };

export default function Carousel({
  items,
  baseWidth = 350,
  loop = true,
  round = false,
  headerType,
}) {
  const containerPadding = 1;
  const itemWidth = baseWidth - containerPadding * 2;
  const trackItemOffset = itemWidth + GAP;
  const carouselItems = loop ? [...items, items[0]] : items;
  const [currentIndex, setCurrentIndex] = useState(0);
  const x = useMotionValue(0);
  const [isMeasured, setIsMeasured] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const containerRef = useRef(null);
  // console.log(items)
  // const headerType = items[0]

  useEffect(() => {
    setMaxHeight(0);
    setIsMeasured(false);
  }, [items]);

  useLayoutEffect(() => {
    if (containerRef.current && !isMeasured) {
      const nodes = containerRef.current.querySelectorAll(
        "[data-carousel-item]"
      );
      let newMaxHeight = 0;
      nodes.forEach((node) => {
        newMaxHeight = Math.max(newMaxHeight, node.offsetHeight);
      });
      if (newMaxHeight > 0 && newMaxHeight !== maxHeight) {
        setMaxHeight(newMaxHeight);
        setIsMeasured(true);
      }
    }
  }, [isMeasured]);

  const effectiveTransition = isResetting ? { duration: 0 } : SPRING_OPTIONS;

  const handleAnimationComplete = () => {
    if (loop && currentIndex === carouselItems.length - 1) {
      setIsResetting(true);
      x.set(0);
      setCurrentIndex(0);
      setTimeout(() => setIsResetting(false), 50);
    }
  };

  const handleDragEnd = (_, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    if (offset < -DRAG_BUFFER || velocity < -VELOCITY_THRESHOLD) {
      if (loop && currentIndex === items.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setCurrentIndex((prev) => Math.min(prev + 1, carouselItems.length - 1));
      }
    } else if (offset > DRAG_BUFFER || velocity > VELOCITY_THRESHOLD) {
      if (loop && currentIndex === 0) {
        setCurrentIndex(items.length - 1);
      } else {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const dragProps = loop
    ? {}
    : {
        dragConstraints: {
          left: -trackItemOffset * (carouselItems.length - 1),
          right: 0,
        },
      };

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden flex flex-col justify-center items-center ${
        round
          ? "rounded-full border border-white"
          : "rounded-customBorderRadius"
      }`}
      style={{
        width: `${baseWidth}px`,
        ...(round && { height: `${baseWidth}px` }),
      }}
    >
      <motion.div
        className="flex"
        drag="x"
        {...dragProps}
        style={{
          width: itemWidth,
          gap: `${GAP}px`,
          perspective: 1000,
          perspectiveOrigin: `${
            currentIndex * trackItemOffset + itemWidth / 2
          }px 50%`,
          x,
        }}
        onDragEnd={handleDragEnd}
        animate={{ x: -(currentIndex * trackItemOffset) }}
        transition={effectiveTransition}
        onAnimationComplete={handleAnimationComplete}
      >
        {carouselItems.map((item, index) => (
          <CarouselItem
            key={index}
            item={item}
            index={index}
            trackItemOffset={trackItemOffset}
            itemWidth={itemWidth}
            x={x}
            round={round}
            headerType={headerType}
            maxHeight={maxHeight}
          />
        ))}
      </motion.div>
      <div
        className={`flex w-full justify-center ${
          round ? "absolute z-20 bottom-12 left-1/2 -translate-x-1/2" : ""
        }`}
      >
        <div className="mt-4 flex w-[200px] h-[12px] justify-center px-8">
          {items.map((_, index) => (
            <div className="mx-1"  key={`handle-carousel-${index}`}>
            <motion.div
              key={index}
              className={`h-2 w-2 rounded-full cursor-pointer transition-colors duration-150 ${
                currentIndex % items.length === index
                  ? round
                    ? "bg-white"
                    : "bg-[#a09f9f]"
                  : round
                  ? "bg-[#555]"
                  : "bg-[rgba(51,51,51,0.4)]"
              }`}
              animate={{
                scale: currentIndex % items.length === index ? 1.2 : 1,
              }}
              onClick={() => setCurrentIndex(index)}
              transition={{ duration: 0.15 }}
            />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
